/**
 * Tabs block. Plain HTMLElement (not the theme's Component base class) so a
 * missing/late-arriving button never throws and breaks the whole component —
 * it just no-ops until buttons show up.
 */
class TabsBlock extends HTMLElement {
  #controller = new AbortController();

  /** @returns {HTMLElement | null} */
  get #nav() {
    return this.querySelector('.tabs-block__nav');
  }

  /** @returns {HTMLButtonElement[]} */
  get #buttons() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-tabs-button]')]);
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
  }

  /** @returns {HTMLElement | null} */
  get #scrollbarThumb() {
    return this.querySelector('.tabs-block__scrollbar-thumb');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    this.#hoistButtons();

    const buttons = this.#buttons;
    if (buttons.length === 0) return;

    buttons.forEach((button) => {
      button.addEventListener('click', this.#handleClick, { signal });
    });

    this.#activate(buttons[0]);

    const nav = this.#nav;
    if (nav && this.#scrollbarThumb) {
      nav.addEventListener('scroll', this.#updateScrollbar, { signal, passive: true });
      window.addEventListener('resize', this.#updateScrollbar, { signal });
      this.#updateScrollbar();
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** Moves each tab's button (authored inside its own `_tabs-panel` block, so
   * it can read that panel's own settings) into the shared nav row. Safe to
   * call repeatedly — already-moved buttons are skipped. */
  #hoistButtons() {
    const nav = this.#nav;
    if (!nav) return;

    this.#buttons.forEach((button) => {
      if (button.parentElement === nav) return;
      nav.appendChild(button);
    });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    this.#activate(button);
  };

  /** @param {HTMLButtonElement} activeButton */
  #activate(activeButton) {
    const targetPanelId = activeButton.getAttribute('aria-controls');

    this.#buttons.forEach((button) => {
      button.setAttribute('aria-selected', String(button === activeButton));
    });

    this.#panels.forEach((panel) => {
      panel.hidden = panel.id !== targetPanelId;
    });
  }

  /** Sizes and positions the custom scrollbar thumb to match the nav's
   * current scroll position, mirroring a native scrollbar thumb. */
  #updateScrollbar = () => {
    const nav = this.#nav;
    const thumb = this.#scrollbarThumb;
    if (!nav || !thumb) return;

    const { scrollWidth, clientWidth, scrollLeft } = nav;

    if (scrollWidth <= clientWidth) {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
      return;
    }

    const thumbRatio = clientWidth / scrollWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    const scrollRatio = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft : 0;
    const maxTranslatePercent = (1 - thumbRatio) * 100;

    thumb.style.width = `${thumbRatio * 100}%`;
    thumb.style.transform = `translateX(${scrollRatio * maxTranslatePercent}%)`;
  };
}

if (!customElements.get('tabs-block')) {
  customElements.define('tabs-block', TabsBlock);
}

class TabsPanel extends HTMLElement {}

if (!customElements.get('tabs-panel')) {
  customElements.define('tabs-panel', TabsPanel);
}
